/**
 * MarkdownRenderer component renders markdown content with syntax highlighting.
 * Uses react-markdown with remark-gfm for GitHub-flavored markdown support.
 */
import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import remarkGfm from "remark-gfm";

interface MarkdownRendererProps {
	/** Markdown content to render */
	content: string;
	/** Optional className for the container */
	className?: string;
}

/**
 * Custom renderers for markdown elements.
 * Applies Tailwind prose classes and custom styling.
 */
const components: Components = {
	// Code blocks with syntax highlighting
	code: ({ className, children, ...props }) => {
		const match = /language-(\w+)/.exec(className ?? "");
		const language = match?.[1] ?? "";
		const isInline = !match;

		// Get string content from children
		const content = typeof children === "string"
			? children
			: Array.isArray(children)
				? children.join("")
				: "";

		if (isInline) {
			return (
				<code className="rounded bg-muted px-1.5 py-0.5 font-mono text-sm" {...props}>
					{content}
				</code>
			);
		}

		return (
			<div className="group relative my-4">
				{/* Language label */}
				{language && (
					<div className="rounded-t-lg bg-muted px-4 py-2 text-xs font-medium text-muted-foreground">
						{language}
					</div>
				)}

				{/* Code block with syntax highlighting */}
				<SyntaxHighlighter
					// @ts-expect-error - style type mismatch between prism and react-syntax-highlighter
					style={oneDark}
					language={language}
					PreTag="div"
					className={!language ? "!mt-0 !rounded-lg" : "!mt-0 !rounded-t-none"}
					customStyle={{
						margin: 0,
						fontSize: "0.875rem",
						lineHeight: "1.5",
					}}
					{...props}
				>
					{content.replace(/\n$/, "")}
				</SyntaxHighlighter>
			</div>
		);
	},

	// Tables with proper styling
	table: ({ children }) => (
		<div className="my-4 overflow-x-auto">
			<table className="min-w-full divide-y divide-border rounded-lg border">
				{children}
			</table>
		</div>
	),
	thead: ({ children }) => <thead className="bg-muted">{children}</thead>,
	tbody: ({ children }) => <tbody className="divide-y divide-border bg-background">{children}</tbody>,
	tr: ({ children }) => <tr className="border-b">{children}</tr>,
	th: ({ children }) => (
		<th className="px-4 py-3 text-left text-sm font-semibold text-foreground">{children}</th>
	),
	td: ({ children }) => <td className="px-4 py-3 text-sm text-foreground">{children}</td>,

	// Links with proper styling
	a: ({ href, children }) => (
		<a
			href={href}
			target="_blank"
			rel="noopener noreferrer"
			className="text-primary underline-offset-4 hover:underline"
		>
			{children}
		</a>
	),

	// Headings with proper hierarchy
	h1: ({ children }) => (
		<h1 className="mb-4 mt-6 text-2xl font-bold text-foreground">{children}</h1>
	),
	h2: ({ children }) => (
		<h2 className="mb-3 mt-5 text-xl font-semibold text-foreground">{children}</h2>
	),
	h3: ({ children }) => (
		<h3 className="mb-2 mt-4 text-lg font-semibold text-foreground">{children}</h3>
	),
	h4: ({ children }) => (
		<h4 className="mb-2 mt-3 text-base font-semibold text-foreground">{children}</h4>
	),

	// Lists
	ul: ({ children }) => <ul className="my-2 ml-6 list-disc space-y-1">{children}</ul>,
	ol: ({ children }) => <ol className="my-2 ml-6 list-decimal space-y-1">{children}</ol>,
	li: ({ children }) => <li className="text-sm leading-relaxed">{children}</li>,

	// Paragraphs
	p: ({ children }) => <p className="my-2 text-sm leading-relaxed text-foreground">{children}</p>,

	// Blockquotes
	blockquote: ({ children }) => (
		<blockquote className="my-4 border-l-4 border-muted-foreground pl-4 italic text-muted-foreground">
			{children}
		</blockquote>
	),

	// Horizontal rules
	hr: () => <hr className="my-6 border-border" />,

	// Inline code (fallback, handled in code component above)
	strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
	em: ({ children }) => <em className="italic">{children}</em>,
};

/**
 * MarkdownRenderer renders markdown content with full formatting support.
 *
 * Features:
 * - GitHub-flavored markdown (tables, task lists, strikethrough)
 * - Syntax highlighting for code blocks
 * - Language labels on code blocks
 * - Styled tables with horizontal scroll
 * - Proper heading hierarchy
 * - Links open in new tab
 * @param props - Component props
 * @param props.content - Markdown string to render
 * @param props.className - Optional CSS classes for container
 */
export function MarkdownRenderer({ content, className }: MarkdownRendererProps) {
	return (
		<div className={className}>
			<ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>
				{content}
			</ReactMarkdown>
		</div>
	);
}
